/**
 * The service card — what the customer sees after Tommy says "I've passed
 * this to our team".
 *
 * ## Why this exists
 *
 * When Tommy's manager takes an inquiry rather than paging the live
 * concierge (see server/tommy-consult.js), Tommy makes the customer a
 * promise: the team will come back within 24 hours, and they don't need to
 * chase it. Without this card that promise is invisible — the customer
 * closes the chat and has nothing but a memory of a bot saying "soon",
 * which is exactly the anxiety that makes people text a human anyway.
 *
 * So the card is the receipt. It sits on their trip page and says we're on
 * it. When an admin answers from the daily report, the answer replaces it
 * in place. They dismiss it when they're done reading, and it's gone for
 * good.
 *
 * That loop is what makes the queue humane rather than a black hole, and
 * it's why "queued" is an acceptable outcome at all.
 *
 * ## Note on what's rendered
 *
 * Only the customer-safe columns come back from `/api/concierge-inquiries`
 * — Stephanie's internal write-up (`summary`, `recommendation`) is
 * deliberately not in that payload. Don't add it here; the whole point of
 * the split is that her candid notes never reach this screen.
 *
 * Renders nothing when there are no live inquiries, so it is safe to mount
 * unconditionally on any trip surface.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, CheckCircle2, X, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useIsMounted } from '@/hooks/useIsMounted';
import { releaseWindow } from '../../../supabase/functions/_shared/confirmation-release-window.js';
import { scheduledDateLabel } from '@/utils/scheduledRequestDate';

/** Fallback headline per category for rows filed before titles existed
 *  (migration 101) or when the title line was missed. */
const CATEGORY_LABELS = {
  seats: 'Seat request',
  baggage: 'Baggage request',
  upgrade: 'Cabin upgrade request',
  flight_change: 'Flight change request',
  miles: 'Miles request',
  gift_card: 'E-gift card redemption',
  contact: 'Contact page message',
  email: 'Email to the team',
  confirmation_release: 'Confirmation number release',
  account_deletion: 'Account deletion request',
  billing: 'Billing question',
  booking: 'Booking question',
  cancellation: 'Cancellation request',
  default_resolution: 'Trip resolution request',
  policy: 'Policy question',
  fare_rules: 'Fare question',
  feedback: 'Feedback',
  other: 'Service request',
};

/**
 * When a pending request's answer arrives. A request the team has scheduled
 * for a specific day (`scheduled_for`) says that day. Otherwise a
 * confirmation release is worked by the accounting team Monday through
 * Friday, so one filed on a Friday afternoon or over the weekend lands
 * Monday, Tuesday at the latest.
 */
export function pendingNote(inq) {
  const scheduled = scheduledDateLabel(inq?.scheduled_for);
  if (scheduled) {
    return inq?.category === 'confirmation_release'
      ? `Your confirmation number is scheduled to be released on ${scheduled}. We'll email you as soon as it's ready.`
      : `Our team has scheduled this for ${scheduled}. We'll email you a copy when it lands.`;
  }
  const w = inq?.category === 'confirmation_release' ? releaseWindow(inq.created_at) : null;
  if (w?.waitsForMonday) {
    return `Our accounting team releases confirmation numbers Monday through Friday, so this arrives ${w.mondayLabel}, or ${w.tuesdayLabel} at the latest — we'll email you a copy when it lands.`;
  }
  return "The answer posts here within 24 hours — we'll email you a copy when it lands.";
}

/** AI-written topic first; category label second; never the raw message
 *  snippet — that's how cards ended up headlined "Ok". */
function inquiryTitle(inq) {
  return inq.title || CATEGORY_LABELS[inq.category] || 'Service request';
}

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

/**
 * @param {string} [bookingReference] - scope to one trip. Omit on the
 *   Trips index to show account-level inquiries instead.
 */
export default function ServiceInquiryCard({ bookingReference = null }) {
  /** The load outlives the card if the trip closes mid-request. See useIsMounted. */
  const isMounted = useIsMounted();
  const [inquiries, setInquiries] = useState([]);
  const [dismissing, setDismissing] = useState(null);

  const authHeader = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      return data?.session?.access_token ? `Bearer ${data.session.access_token}` : null;
    } catch {
      return null;
    }
  }, []);

  const load = useCallback(async () => {
    const header = await authHeader();
    if (!header || !isMounted()) return;
    try {
      const qs = bookingReference ? `?ref=${encodeURIComponent(bookingReference)}` : '';
      const res = await fetch(`/api/concierge-inquiries${qs}`, { headers: { Authorization: header } });
      if (!res.ok) return;
      const data = await res.json();
      if (!isMounted()) return;
      setInquiries(Array.isArray(data?.inquiries) ? data.inquiries : []);
    } catch {
      // A card that fails to load is invisible, which is the right
      // degradation — never block the trip page over it.
    }
  }, [authHeader, bookingReference, isMounted]);

  useEffect(() => { load(); }, [load]);

  // Same-page filings (e.g. the "Finished paying?" CTA creating a
  // Confirmation Number Release inquiry) announce themselves so the card
  // appears without a reopen.
  useEffect(() => {
    const refresh = () => { load(); };
    window.addEventListener('defera:inquiries-updated', refresh);
    return () => window.removeEventListener('defera:inquiries-updated', refresh);
  }, [load]);

  const dismiss = useCallback(async (id) => {
    setDismissing(id);
    // Optimistic: the row is already gone from their view either way, and
    // a dismiss that silently fails server-side is not worth a spinner
    // they'd have to watch.
    setInquiries((prev) => prev.filter((i) => i.id !== id));
    try {
      const header = await authHeader();
      if (!header) return;
      await fetch(`/api/concierge-inquiries/${encodeURIComponent(id)}/dismiss`, {
        method: 'POST',
        headers: { Authorization: header },
      });
    } catch {
      /* already removed from view */
    } finally {
      setDismissing(null);
    }
  }, [authHeader]);

  if (!inquiries.length) return null;

  return (
    <div className="space-y-2.5">
      <AnimatePresence initial={false}>
        {inquiries.map((inq) => {
          const resolved = inq.status === 'resolved';
          return (
            <motion.div
              key={inq.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.22 }}
              className={`rounded-2xl border overflow-hidden ${
                resolved
                  ? 'border-emerald-400/25 bg-emerald-500/[0.06]'
                  : 'border-white/[0.08] bg-white/[0.035]'
              }`}
            >
              <div className="px-4 py-3.5">
                <div className="flex items-center gap-2 mb-2">
                  {resolved ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300/90 flex-shrink-0" strokeWidth={2} />
                  ) : (
                    <Loader2 className="w-3.5 h-3.5 text-white/50 flex-shrink-0 animate-spin" strokeWidth={2} />
                  )}
                  {/* "Submitted · in progress" over "we're on it": the card
                      is the customer's proof the request is filed and
                      moving, which is the whole reason Tommy can point at
                      it instead of promising a callback. */}
                  <span className={`text-[10.5px] uppercase tracking-wider font-semibold ${
                    resolved ? 'text-emerald-300/90' : 'text-white/50'
                  }`}
                  >
                    {resolved ? 'Answered' : 'Submitted · in progress'}
                  </span>
                  <span className="ml-auto text-[10.5px] text-white/30">
                    {timeAgo(resolved ? inq.resolved_at : inq.created_at)}
                  </span>
                  {/* Dismiss is available at any point — a customer who has
                      lost interest in their own question shouldn't have to
                      wait for us to answer it before clearing the card. */}
                  <button
                    type="button"
                    onClick={() => dismiss(inq.id)}
                    disabled={dismissing === inq.id}
                    aria-label="Dismiss this request"
                    className="text-white/30 hover:text-white/70 transition-colors disabled:opacity-40"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </div>

                <p className="text-[13.5px] font-medium text-white/90 leading-snug">
                  {inquiryTitle(inq)}
                </p>

                {resolved ? (
                  <div className="mt-2.5 pt-2.5 border-t border-emerald-400/15">
                    <p className="text-[13.5px] text-white/90 leading-relaxed whitespace-pre-wrap">
                      {inq.resolution_note}
                    </p>
                  </div>
                ) : (
                  <p className="mt-1.5 text-[11.5px] text-white/60 inline-flex items-start gap-1.5">
                    <Clock className="w-3 h-3 shrink-0 mt-[2.5px]" strokeWidth={1.75} />
                    {pendingNote(inq)}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
